// =====================================================================
// ErrorDashboard.jsx — KPIs + bar charts (M-006)
// =====================================================================
import { useState, useEffect } from 'react';
import { errors as errorsApi } from '../../services/api.js';

function ymd(d) { return d.toISOString().slice(0, 10); }
function firstOfMonth() { const d = new Date(); d.setUTCDate(1); return ymd(d); }
function lastOfMonth() {
  const d = new Date();
  return ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
}

export default function ErrorDashboard() {
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(lastOfMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  async function load() {
    setLoading(true); setErr(null);
    try {
      const d = await errorsApi.stats({ from, to });
      setData(d);
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [from, to]);

  if (loading) return <div style={{ padding: 24, color: '#64748b' }}>Chargement du tableau de bord…</div>;
  if (err) return <div style={{ padding: 12, background: '#fef2f2', color: '#b91c1c', borderRadius: 6 }}>Erreur: {err}</div>;
  if (!data) return null;

  const maxOpCount = Math.max(1, ...data.byOperator.map(b => b.count));
  const maxTypCount = Math.max(1, ...data.byTypology.map(b => b.count));
  const respTotal = data.byResponsibility.YES + data.byResponsibility.NO + data.byResponsibility.TBD;
  const respPct = respTotal > 0 ? Math.round(100 * data.byResponsibility.YES / respTotal) : 0;

  return (
    <div>
      {/* Date filter */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 18, fontSize: 13 }}>
        <span style={{ color: '#64748b' }}>Période :</span>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
        <span style={{ color: '#94a3b8' }}>→</span>
        <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
        <button onClick={() => { setFrom(firstOfMonth()); setTo(lastOfMonth()); }} style={btnSecondary}>Ce mois</button>
        <button onClick={() => {
          const d = new Date(); d.setUTCMonth(d.getUTCMonth() - 1);
          const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
          setFrom(ymd(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))));
          setTo(ymd(last));
        }} style={btnSecondary}>Mois passé</button>
        <button onClick={() => {
          const d = new Date(); setFrom(`${d.getUTCFullYear()}-01-01`); setTo(`${d.getUTCFullYear()}-12-31`);
        }} style={btnSecondary}>Année</button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        <Kpi label="Incidents" value={data.totalIncidents} suffix={data.totalIncidents > 1 ? 'erreurs' : 'erreur'} color="#dc2626" />
        <Kpi label="Coût total" value={`${data.totalCost.toFixed(0)} €`} suffix={`${data.byTypology.reduce((s,b)=>s+(b.cost>0?1:0),0)} avec coût chiffré`} color="#ea580c" />
        <Kpi label="Resp. Brain E-Log" value={`${data.byResponsibility.YES}`} suffix={`${respPct}% du total`} color="#9333ea" />
        <Kpi label="Opérateur le + impacté" value={data.byOperator[0]?.operatorName?.split(' ')[0] || '—'} suffix={data.byOperator[0] ? `${data.byOperator[0].count} erreur${data.byOperator[0].count > 1 ? 's' : ''}` : ''} color="#0891b2" smallValue />
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16 }}>
        <div style={chartBox}>
          <h3 style={chartTitle}>Top typologies</h3>
          {data.byTypology.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, padding: 12 }}>Aucune donnée</div>}
          {data.byTypology.slice(0, 10).map(b => (
            <div key={b.typologyId} style={barRow}>
              <div style={{ ...barLabel, flex: 1, minWidth: 220 }}>{b.typologyLabel.slice(0, 60)}</div>
              <div style={{ flex: 2, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${100 * b.count / maxTypCount}%`, height: '100%', background: 'linear-gradient(90deg, #c7d2fe, #4338ca)' }} />
              </div>
              <div style={barVal}>{b.count}</div>
              {b.cost > 0 && <div style={{ ...barVal, color: '#94a3b8', fontSize: 11 }}>{b.cost.toFixed(0)} €</div>}
            </div>
          ))}
        </div>
        <div style={chartBox}>
          <h3 style={chartTitle}>Par opérateur (top 10)</h3>
          {data.byOperator.length === 0 && <div style={{ color: '#94a3b8', fontSize: 13, padding: 12 }}>Aucune donnée</div>}
          {data.byOperator.slice(0, 10).map((b, idx) => {
            const colorTier = idx < 2 ? '#b91c1c' : idx < 5 ? '#ea580c' : '#ca8a04';
            const colorBg = idx < 2 ? '#fecaca' : idx < 5 ? '#fed7aa' : '#fde68a';
            return (
              <div key={b.operatorUserId} style={barRow}>
                <div style={{ ...barLabel, width: 130 }}>{b.operatorName.slice(0, 18)}</div>
                <div style={{ flex: 1, height: 18, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ width: `${100 * b.count / maxOpCount}%`, height: '100%', background: `linear-gradient(90deg, ${colorBg}, ${colorTier})` }} />
                </div>
                <div style={barVal}>{b.count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Responsibility breakdown */}
      <div style={{ ...chartBox, marginTop: 16 }}>
        <h3 style={chartTitle}>Responsabilité Brain E-Log</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, padding: 12, borderRadius: 8, background: '#fee2e2', minWidth: 180 }}>
            <div style={{ fontSize: 11, color: '#7f1d1d', fontWeight: 600, textTransform: 'uppercase' }}>Oui (notre responsabilité)</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#b91c1c' }}>{data.byResponsibility.YES}</div>
          </div>
          <div style={{ flex: 1, padding: 12, borderRadius: 8, background: '#dcfce7', minWidth: 180 }}>
            <div style={{ fontSize: 11, color: '#14532d', fontWeight: 600, textTransform: 'uppercase' }}>Non</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#166534' }}>{data.byResponsibility.NO}</div>
          </div>
          <div style={{ flex: 1, padding: 12, borderRadius: 8, background: '#fef3c7', minWidth: 180 }}>
            <div style={{ fontSize: 11, color: '#713f12', fontWeight: 600, textTransform: 'uppercase' }}>À déterminer</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#92400e' }}>{data.byResponsibility.TBD}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, suffix, color, smallValue }) {
  return (
    <div style={{ background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.5px', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: smallValue ? 18 : 28, fontWeight: 700, color: color || '#1e293b', marginTop: 4, lineHeight: 1 }}>{value}</div>
      {suffix && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{suffix}</div>}
    </div>
  );
}

const inputStyle = { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 };
const btnSecondary = { padding: '5px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, cursor: 'pointer' };
const chartBox = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 16 };
const chartTitle = { margin: '0 0 12px', fontSize: 13, color: '#475569', fontWeight: 600 };
const barRow = { display: 'flex', alignItems: 'center', gap: 10, margin: '6px 0', fontSize: 12 };
const barLabel = { fontSize: 11, color: '#475569', flexShrink: 0 };
const barVal = { width: 40, textAlign: 'right', fontWeight: 600, fontSize: 12 };
